import { useEffect, useRef, useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  fetchActivePermissionPages,
  fetchMyRolePermissions,
  setPermissionsBypass,
} from "@/features/permissions/permissionsSlice";
import { isFrontendRbacDisabled } from "@/utils/rbacToggle";
import {
  getEffectiveUser,
  isSalesmanPathAllowed,
  isSalesmanRole,
  shouldBypassPermissionMatrix,
} from "@/utils/permissionsUser";
import { getDashboardRoute } from "@/utils/onboardingUtils";

/**
 * Salesman roles only reach the hardcoded pages. Other roles see every page for now.
 */
export default function RoutePermissionGate({ children }) {
  const rbacDisabled = isFrontendRbacDisabled();
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((s) => s.auth.user);
  const token = useSelector((s) => s.auth.token);
  const bypass = useSelector((s) => s.permissions.bypass);
  const permStatus = useSelector((s) => s.permissions.status);
  const permError = useSelector((s) => s.permissions.error);
  const activePagesStatus = useSelector((s) => s.permissions.activePagesStatus);
  const activePagesError = useSelector((s) => s.permissions.activePagesError);
  const toastShownForErrorRef = useRef(null);
  const pagesToastShownRef = useRef(null);

  const effectiveUser = useMemo(() => getEffectiveUser(authUser), [authUser]);
  const bypassesMatrix = useMemo(
    () => shouldBypassPermissionMatrix(effectiveUser),
    [effectiveUser]
  );

  useEffect(() => {
    if (rbacDisabled) return;
    if (!token) return;
    const u = getEffectiveUser(authUser);
    if (!u) return;

    dispatch(fetchActivePermissionPages());

    if (shouldBypassPermissionMatrix(u)) {
      dispatch(setPermissionsBypass(true));
      return;
    }

    const roleId = u.company_role_id ?? u.companyRoleId;
    if (roleId != null && roleId !== "") {
      dispatch(fetchMyRolePermissions(Number(roleId)));
    } else {
      dispatch(setPermissionsBypass(true));
    }
  }, [rbacDisabled, token, authUser, authUser?.company_role_id, authUser?.id, dispatch]);

  useEffect(() => {
    if (rbacDisabled) return;
    if (permStatus === "succeeded" || permStatus === "idle") {
      toastShownForErrorRef.current = null;
      return;
    }
    if (permStatus !== "failed" || !permError || bypass || bypassesMatrix) return;
    if (toastShownForErrorRef.current === permError) return;
    toastShownForErrorRef.current = permError;
    toast.error(permError, { toastId: "permissions-fetch-failed" });
  }, [rbacDisabled, permStatus, permError, bypass, bypassesMatrix]);

  useEffect(() => {
    if (rbacDisabled) return;
    if (activePagesStatus === "succeeded" || activePagesStatus === "idle") {
      pagesToastShownRef.current = null;
      return;
    }
    if (activePagesStatus !== "failed" || !activePagesError) return;
    if (pagesToastShownRef.current === activePagesError) return;
    pagesToastShownRef.current = activePagesError;
    toast.error(activePagesError, { toastId: "permission-pages-fetch-failed" });
  }, [rbacDisabled, activePagesStatus, activePagesError]);

  if (isSalesmanRole(effectiveUser)) {
    if (!isSalesmanPathAllowed(location.pathname)) {
      return <Navigate to={getDashboardRoute()} replace />;
    }
    return children;
  }

  return children;
}
